import React, { useState, useEffect, useCallback } from 'react';
import config from '../utils/config';
import axiosInstance from '../utils/axiosInstance';
import { isLocal } from '../utils/isLocal';
import { useAuth } from '../context/AuthContext';
import { useDevices } from '../hooks/useDevices';
import { formatTimestamp } from '../utils/formatTimestamp';
import Button from '../components/ui/Button';
import { Input } from '../components/pilot-ui/input';
import { Label } from '../components/pilot-ui/label';

interface Breadcrumb {
  label: string;
  type: 'root' | 'collection' | 'document' | 'subcollection';
  path?: string;
  collection?: string;
}

// Friendly names for the known FluxHive Firestore collections, so the explorer
// reads "HVAC Systems" / "Device" rather than the raw collection id.
const COLLECTION_LABELS: Record<string, string> = {
  users: 'Users',
  locations: 'Locations',
  hvac_systems: 'HVAC Systems',
  ac_systems: 'AC Systems',
  devices: 'Devices',
  filters: 'Filters',
};
const COLLECTION_SINGULAR: Record<string, string> = {
  users: 'User',
  locations: 'Location',
  hvac_systems: 'HVAC System',
  ac_systems: 'AC System',
  devices: 'Device',
  filters: 'Filter',
};
const collectionLabel = (name?: string) => (name ? COLLECTION_LABELS[name] ?? name : '');
const collectionSingular = (name?: string) => (name ? COLLECTION_SINGULAR[name] ?? name : 'Document');

const FirestoreExplorerPage = () => {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('firestore_write');
  const { devices } = useDevices();
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([{ label: 'Root', type: 'root' }]);
  const [data, setData] = useState<any>(null);
  const [subcollections, setSubcollections] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState(100);
  const [filterInput, setFilterInput] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const currentBreadcrumb = breadcrumbs[breadcrumbs.length - 1];

  const fetchData = useCallback(async (crumb: Breadcrumb, docLimit: number, where?: string) => {
    setLoading(true);
    setError('');
    setSubcollections([]);
    try {
      const baseUrl = config.firestoreQueryApiUrl!;
      if (crumb.type === 'root') {
        const res = await axiosInstance.get(baseUrl, { params: { mode: 'collections' } });
        setData(res.data);
      } else if (crumb.type === 'collection' || crumb.type === 'subcollection') {
        const params: any = {
          mode: 'documents',
          collection: crumb.collection,
          limit: docLimit,
        };
        if (crumb.type === 'subcollection' && crumb.path) {
          params.path = crumb.path;
        }
        if (where) {
          params.where = where;
        }
        const res = await axiosInstance.get(baseUrl, { params });
        setData(res.data);
      } else if (crumb.type === 'document' && crumb.path) {
        // Document data is passed directly via setData — only fetch subcollections
        const subRes = await axiosInstance.get(baseUrl, { params: { mode: 'subcollections', path: crumb.path } });
        const subs = Array.isArray(subRes.data) ? subRes.data : (subRes.data?.subcollections || []);
        setSubcollections(subs);
      }
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setError(axiosError?.response?.data?.error || 'Failed to fetch data.');
      if (crumb.type !== 'document') setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(currentBreadcrumb, limit);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateTo = (crumb: Breadcrumb) => {
    setBreadcrumbs(prev => [...prev, crumb]);
    setFilterInput('');
    setSortColumn(null);
    fetchData(crumb, limit);
  };

  const navigateToDocument = (crumb: Breadcrumb, docData: any) => {
    setBreadcrumbs(prev => [...prev, crumb]);
    setFilterInput('');
    setSortColumn(null);
    setData(docData);
    // Only fetch subcollections for the document (no single-doc fetch mode in API)
    fetchData(crumb, limit);
  };

  const navigateToBreadcrumb = (index: number) => {
    const target = breadcrumbs[index];
    // If navigating back to a document breadcrumb, go to its parent collection instead
    // since we can't re-fetch a single document
    if (target.type === 'document' && index > 0) {
      const newBreadcrumbs = breadcrumbs.slice(0, index);
      setBreadcrumbs(newBreadcrumbs);
      setFilterInput('');
      setSortColumn(null);
      fetchData(newBreadcrumbs[newBreadcrumbs.length - 1], limit);
    } else {
      const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
      setBreadcrumbs(newBreadcrumbs);
      setFilterInput('');
      setSortColumn(null);
      fetchData(newBreadcrumbs[newBreadcrumbs.length - 1], limit);
    }
  };

  // Breadcrumb display: friendly names for collection segments (hvac_systems →
  // "HVAC Systems"); document/root segments keep their raw id/label.
  const crumbDisplay = (crumb: Breadcrumb) =>
    crumb.type === 'collection' || crumb.type === 'subcollection'
      ? collectionLabel(crumb.collection ?? crumb.label)
      : crumb.label;

  const handleCollectionClick = (collectionName: string) => {
    navigateTo({ label: collectionName, type: 'collection', collection: collectionName });
  };

  const handleDocumentClick = (doc: any, collectionName: string) => {
    const docId = doc._id || doc.id;
    // Prefer _path from API; fall back to constructing from breadcrumb context
    const path = doc._path || (currentBreadcrumb.path
      ? `${currentBreadcrumb.path}/${collectionName}/${docId}`
      : `${collectionName}/${docId}`);
    navigateToDocument({ label: docId, type: 'document', path }, doc);
  };

  const handleSubcollectionClick = (subcollectionName: string) => {
    navigateTo({
      label: subcollectionName,
      type: 'subcollection',
      collection: subcollectionName,
      path: currentBreadcrumb.path, // parent document path
    });
  };

  const handleFilter = () => {
    fetchData(currentBreadcrumb, limit, filterInput || undefined);
  };

  const handleDelete = async (path: string) => {
    try {
      await axiosInstance.delete(config.firestoreQueryApiUrl!, { params: { path } });
      setDeleteConfirm(null);
      // Navigate back to parent collection
      if (breadcrumbs.length > 1) {
        navigateToBreadcrumb(breadcrumbs.length - 2);
      }
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setError(axiosError?.response?.data?.error || 'Failed to delete document.');
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortValue = (value: any): string | number => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object' && value._seconds !== undefined) return value._seconds;
    if (typeof value === 'number') return value;
    if (typeof value === 'boolean') return value ? 1 : 0;
    return String(value).toLowerCase();
  };

  const sortDocs = (docs: any[]): any[] => {
    if (!sortColumn) return docs;
    return [...docs].sort((a, b) => {
      const aVal = getSortValue(sortColumn === '_id' ? (a._id || a.id) : a[sortColumn]);
      const bVal = getSortValue(sortColumn === '_id' ? (b._id || b.id) : b[sortColumn]);
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const PREFERRED_COLUMNS: Record<string, string[]> = {
    users: ['email', 'firstName', 'lastName'],
  };

  const getDocumentColumns = (docs: any[]): string[] => {
    if (!docs || docs.length === 0) return [];
    const collectionName = currentBreadcrumb.collection || '';
    const preferred = PREFERRED_COLUMNS[collectionName];
    if (preferred) return preferred;

    const allKeys = new Set<string>();
    docs.slice(0, 20).forEach(doc => {
      Object.keys(doc).forEach(key => {
        if (key !== '_id' && key !== '_path' && key !== 'id') allKeys.add(key);
      });
    });
    return Array.from(allKeys).slice(0, 5);
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') {
      // Firestore timestamp
      if (value._seconds !== undefined) {
        return formatTimestamp(value._seconds * 1000);
      }
      return JSON.stringify(value);
    }
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value);
  };

  const truncate = (str: string, max: number) =>
    str.length > max ? str.slice(0, max) + '...' : str;

  const renderCollections = (collections: string[]) => collections.length === 0
    ? <p className="text-sm text-muted-foreground">No collections are available.</p>
    : (
    <div className="space-y-1">
      {collections.map(name => (
        <Button
          key={name}
          onClick={() => handleCollectionClick(name)}
          variant="secondary"
          title={`Open ${collectionLabel(name)} collection`}
          className="w-full justify-start px-4 py-3"
        >
          <span className="text-blue-600 font-medium">{name}</span>
        </Button>
      ))}
    </div>
  );

  const renderSortIndicator = (column: string) => {
    if (sortColumn !== column) return <span className="text-gray-300 ml-1 text-lg">&#x25B4;&#x25BE;</span>;
    return sortDirection === 'asc'
      ? <span className="ml-1 text-lg">&#x25B4;</span>
      : <span className="ml-1 text-lg">&#x25BE;</span>;
  };

  const renderDocumentTable = (docs: any[]) => {
    const columns = getDocumentColumns(docs);
    const collectionName = currentBreadcrumb.collection || '';
    const sorted = sortDocs(docs);
    return (
      <div>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-gray-800">
            {collectionLabel(collectionName)}
            <span className="ml-2 text-sm font-normal text-gray-500">
              {docs.length} document{docs.length !== 1 ? 's' : ''}
            </span>
          </h3>
          {currentBreadcrumb.type === 'subcollection' && currentBreadcrumb.path && (
            <div className="text-xs text-muted-foreground font-mono break-all">
              under {currentBreadcrumb.path}
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('_id')} aria-sort={sortColumn === '_id' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <Button variant="link" title="Sort by document ID" onClick={event => {
                    event.stopPropagation();
                    handleSort('_id');
                  }}>ID{renderSortIndicator('_id')}</Button>
                </th>
                {columns.map(col => (
                  <th
                    key={col}
                    className="text-left py-2 px-3 font-medium text-gray-600 cursor-pointer"
                    onClick={() => handleSort(col)}
                    aria-sort={sortColumn === col ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <Button variant="link" title={`Sort by ${col}`} onClick={event => {
                      event.stopPropagation();
                      handleSort(col);
                    }}>{col}{renderSortIndicator(col)}</Button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((doc, i) => {
                const docId = doc._id || doc.id || `doc-${i}`;
                return (
                  <tr key={docId} onClick={() => handleDocumentClick(doc, collectionName)} className="border-b border-gray-100 cursor-pointer hover:bg-blue-50 transition-colors">
                    <td className="py-2 px-3 text-blue-600 font-medium"><Button variant="link" title={`Open document ${docId}`} onClick={event => {
                      event.stopPropagation();
                      handleDocumentClick(doc, collectionName);
                    }}>{truncate(String(docId), 30)}</Button></td>
                    {columns.map(col => (
                      <td key={col} className="py-2 px-3 text-gray-700">{truncate(formatValue(doc[col]), 50)}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderDocumentDetail = (doc: any) => {
    const docPath = currentBreadcrumb.path || '';
    const entries = Object.entries(doc).filter(([key]) => key !== '_id' && key !== '_path' && key !== 'id');
    // Name the document: "<Singular collection>: <docId>" (+ RDS description for devices).
    const detailCollection = breadcrumbs[breadcrumbs.length - 2]?.collection;
    const docId = currentBreadcrumb.label;
    const deviceDesc = detailCollection === 'devices'
      ? devices.find((d) => d.name === docId)?.description
      : undefined;
    const detailTitle = `${collectionSingular(detailCollection)}: ${docId}${deviceDesc ? ` — ${deviceDesc}` : ''}`;
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-gray-800 break-all">{detailTitle}</h3>
              <div className="text-xs text-muted-foreground">Document fields</div>
            </div>
            {docPath && canWrite && (
              deleteConfirm === docPath ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-red-600">Delete this document?</span>
                  <Button
                    variant="danger"
                    onClick={() => handleDelete(docPath)}
                    title="Delete this Firestore document now; this cannot be undone"
                  >
                    Confirm
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setDeleteConfirm(null)}
                    title="Keep this document; nothing is deleted"
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  variant="danger"
                  onClick={() => setDeleteConfirm(docPath)}
                  title="Delete this Firestore document; you are asked to confirm first"
                >
                  Delete
                </Button>
              )
            )}
          </div>
          <table className="w-full text-sm">
            <tbody>
              {entries.map(([key, value]) => (
                <tr key={key} className="border-b border-gray-100">
                  <td className="py-2 px-3 font-medium text-gray-600 w-1/4 align-top">{key}</td>
                  <td className="py-2 px-3 text-gray-800">
                    {typeof value === 'object' && value !== null ? (
                      <pre className="text-xs bg-gray-50 p-2 rounded-sm overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(value, null, 2)}
                      </pre>
                    ) : (
                      formatValue(value)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {subcollections.length > 0 && (
          <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Subcollections</h3>
            <div className="space-y-1">
              {subcollections.map(name => (
                <Button
                  key={name}
                  onClick={() => handleSubcollectionClick(name)}
                  variant="link"
                  title={`Open ${collectionLabel(name)} subcollection`}
                  className="px-4 py-2"
                >
                  <span className="text-blue-600 font-medium">{name}</span>
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (loading) return <div className="text-gray-500 mt-4">Loading...</div>;
    if (error) return <div role="alert" className="bg-red-50 text-red-700 p-3 rounded-md mt-4">{error} <Button variant="secondary" title="Try loading this collection again" onClick={() => fetchData(currentBreadcrumb, limit, filterInput || undefined)}>Retry</Button></div>;
    if (!data) return null;

    if (currentBreadcrumb.type === 'root') {
      const collections = data.collections || data;
      return Array.isArray(collections) ? renderCollections(collections) : null;
    }

    if (currentBreadcrumb.type === 'collection' || currentBreadcrumb.type === 'subcollection') {
      const docs = data.documents || data;
      return Array.isArray(docs) && docs.length > 0
        ? renderDocumentTable(docs)
        : <div className="text-gray-500 mt-4">No documents found.</div>;
    }

    if (currentBreadcrumb.type === 'document') {
      const doc = data.document || data;
      return renderDocumentDetail(doc);
    }

    return null;
  };

  const isCollectionView = currentBreadcrumb.type === 'collection' || currentBreadcrumb.type === 'subcollection';

  return (
    <div className="h-full w-full bg-gray-50" style={isLocal() ? { border: '4px solid red' } : {}}>
      {isLocal() && (
        <div className="bg-red-600 text-red text-center p-2">
          <strong>Local Environment</strong>
        </div>
      )}
      <div className="max-w-7xl mx-auto p-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold mb-4">Firestore Explorer</h1>

          {/* Breadcrumbs */}
          <div className="flex items-center gap-1 text-sm mb-4 flex-wrap">
            {breadcrumbs.map((crumb, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="text-muted-foreground mx-1">&gt;</span>}
                {i < breadcrumbs.length - 1 ? (
                  <Button
                    variant="link"
                    size="md"
                    onClick={() => navigateToBreadcrumb(i)}
                    title="Go back up to this level of the database"
                  >
                    {crumbDisplay(crumb)}
                  </Button>
                ) : (
                  <span className="text-gray-800 font-medium">{crumbDisplay(crumb)}</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Filter bar for collection views */}
          {isCollectionView && (
            <div className="flex items-end gap-2 mb-4">
              <div className="grow">
                <Label htmlFor="firestore-filter" className="block text-sm font-medium mb-1 text-gray-600">Filter (field:value)</Label>
                <Input
                  id="firestore-filter"
                  type="text"
                  value={filterInput}
                  onChange={e => setFilterInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleFilter()}
                  placeholder="e.g. status:active"
                  className="w-full"
                  title="Filter Firestore documents by one field and value"
                  aria-describedby="firestore-filter-help"
                />
                <p id="firestore-filter-help" className="text-xs text-muted-foreground">Use field:value, then press Enter or Apply.</p>
              </div>
              <div>
                <Label htmlFor="firestore-limit" className="block text-sm font-medium mb-1 text-gray-600">Limit</Label>
                <Input
                  id="firestore-limit"
                  type="number"
                  value={limit}
                  onChange={e => setLimit(Number(e.target.value) || 100)}
                  className="w-20"
                  min={1}
                  max={1000}
                  title="Maximum documents to load, from 1 to 1000"
                  aria-describedby="firestore-limit-help"
                />
                <p id="firestore-limit-help" className="text-xs text-muted-foreground">1–1000 documents.</p>
              </div>
              <Button
                variant="primary"
                onClick={handleFilter}
                title="List this collection again with the filter and limit above (Enter in the filter box does the same)"
              >
                Apply
              </Button>
            </div>
          )}

          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default FirestoreExplorerPage;
